<template>
  <Modal v-bind="$props" @onClose="handleClose()" @onConfirm="handleConfirm()">
    <template #header>
      <h2>Configure Filter Columns</h2>
      <div @click="onClose">
        <Icon name="times" size="24" />
      </div>
    </template>
    <template #content>
      <div class="left-section">
        <div class="left-section-header">Default Filters</div>
        <div
          v-for="(item, key) in configuration"
          :key="key"
          class="left-section-title"
        >
          <div v-if="item.isDefault" class="left-section-tab">
            {{ key }}
          </div>
        </div>
      </div>
      <div class="right-section">
        <div class="right-section-header">Configuration</div>
        <div class="right-section-search">
          <text-input
            v-model="searchTerm"
            :show-clear-btn="true"
            :value="searchTerm"
            :label-sr-only="true"
            placeholder="Search for Column"
            label="Search Dispute"
            icon="magnifying-glass"
            icon-position="left"
          />
        </div>
        <div class="right-section-title">
          <div v-if="filteredCurrentConfigColumnsIndex.length">
            <div
              v-for="item in filteredCurrentConfigColumnsIndex"
              :key="item"
              v-tooltip="getTooltip"
              class="column-heads"
            >
              <div class="column-title">
                <span class="column-value">{{ item }}</span>
              </div>
              <div class="column-icons">
                <span
                  v-if="currentConfigColumns[item].isVisible"
                  class="mi-24px mi-eye"
                  aria-hidden="true"
                  @click="toggleColumnVisibility(currentConfigColumns[item])"
                ></span>
                <span
                  v-else
                  v-tooltip="
                    visibleNumber >= 30 && 'Cannot show more than 30 columns'
                  "
                  class="mi-24px mi-eye-slash"
                  aria-hidden="true"
                  @click="toggleColumnVisibility(currentConfigColumns[item])"
                ></span>
              </div>
            </div>
          </div>
          <div v-else class="no-items-found">
            <span>No Items found, please use correct column names.</span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="lCol" @click="restoreDefaults">
        <span class="restore-defaults">Restore Defaults</span>
      </div>
      <div class="rCol">
        <Button
          label="Cancel"
          appearance="tertiary"
          class="button-group__button"
          @onClick="onClose"
        />
        <Button
          class="button-group__button"
          :label="processing ? 'Applying' : 'Apply'"
          :disabled="processing"
          @onClick="saveColumnConfig"
        />
      </div>
    </template>
  </Modal>
</template>

<script>
// External libraries
import ClickOutside from "vue-click-outside";

// Common components
import { TextInput, Icon, Button, Modal } from "../../components";

export default {
  name: "ConfigureFilterModal",
  directives: {
    ClickOutside,
  },
  components: {
    Button,
    Icon,
    Modal,
    TextInput,
  },
  props: {
    /**
     * @description
     * Modal is visible when value is true
     */
    isOpen: { type: Boolean, default: false },
    /**
     * @description
     * Filter column configuration
     */
    configuration: { type: Object, required: true },
    /**
     * @description
     * Default filter columns
     */
    defaultColumns: { type: Array, required: true },
    /**
     * @description
     * Used as a prefix for storiing the column configuration on localstorage
     */
    cacheName: { type: String, default: "" },
  },
  data() {
    return {
      configColumns: [],
      processing: false,
      visibleNumber: 0,
      searchTerm: "",
      currentConfigColumns: {},
      filteredCurrentConfigColumnsIndex: [],
    };
  },
  computed: {
    /**
     * @description
     * Checks if searchTerm is empty or not
     * @returns {boolean}
     */
    ifSearchTermIsNotEmpty() {
      return this.searchTerm.length ? true : false;
    },
    /**
     * @description
     * If searchTerm is not empty then it returns validation message
     * @returns {string} - Validation message
     */
    getTooltip() {
      return this.searchTerm.length
        ? "You cannot change the position while searching for columns"
        : "";
    },
  },
  watch: {
    /**
     * @description
     * Filteres list of column names whenever searchterm gets changes
     * @param {string} - Search keyword
     * @returns {array} - Returns list of filtered column names based on search keyword
     */
    searchTerm(keyword) {
      this.filteredCurrentConfigColumnsIndex = Object.keys(
        this.currentConfigColumns
      ).filter((i) => i.toLowerCase().indexOf(keyword) > -1);
    },
  },
  mounted() {
    const columnsCached = localStorage.getItem(
      `${this.cacheName}_for_columns"`
    );
    let previousConfig = columnsCached ? JSON.parse(columnsCached) : null;
    const currentConfigColumns = previousConfig
      ? previousConfig
      : JSON.parse(JSON.stringify(this.configuration));
    this.currentConfigColumns = currentConfigColumns;
    this.filteredCurrentConfigColumnsIndex = Object.keys(currentConfigColumns);
  },
  methods: {
    /**
     * @description
     * Triggered on close of modal,
     * Emits onModalClose methods,
     * Resets searchTerm
     */
    onClose() {
      this.$emit("onModalClose");
      this.searchTerm = "";
    },
    /**
     * @description
     * Toggles a selected column visiblity,
     * Column visibility is set to true only when visible columns count is less than 330
     * @param {string} item - Selected column
     */
    toggleColumnVisibility(item) {
      let count = 0;
      Object.keys(this.currentConfigColumns).forEach((item) => {
        if (this.currentConfigColumns[item].isVisible) {
          count++;
        }
      });

      this.visibleNumber = count;

      if (count < 30) {
        item.isVisible = !item.isVisible;
      } else {
        item.isVisible = false;
      }
    },
    /**
     * @description
     * Saves column configurations,
     * Emits onSaveColumnConfig with new configuration as a param,
     * Stores the configuration on localStorage,
     * Emits onModalClose method
     * Resets the searchTerm
     */
    saveColumnConfig() {
      // showToast("success", "Applying Column Configuration");
      const newConfiguration = JSON.parse(
        JSON.stringify(this.currentConfigColumns)
      );
      this.$emit("onSaveColumnConfig", newConfiguration);
      localStorage.setItem(
        `${this.cacheName}_for_columns"`,
        JSON.stringify(newConfiguration)
      );
      this.$emit("onModalClose");
      this.searchTerm = "";
    },
    /**
     * @description
     * Restores to default visible columns
     */
    restoreDefaults() {
      Object.keys(this.currentConfigColumns).forEach((item) => {
        this.currentConfigColumns[item].isVisible =
          this.defaultColumns.indexOf(item) > -1;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/imports.scss";

.modal-fade-enter,
.modal-fade-leave-active {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.5s ease;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.modal-container {
  max-width: 648px;
  width: 100%;
  overflow-x: auto;
  border-radius: 5px;

  & .modal {
    box-shadow: 2px 2px 20px 1px;
    display: flex;
    flex-direction: column;
  }
  @include media("<=720px") {
    position: absolute;
    bottom: 0;
  }
}

.modal-backdrop {
  @include media("<=desktop") {
    left: 0;
    bottom: 0;
    top: auto;
    right: 0;
  }

  &:focus {
    outline: none;
  }

  & .wrapper {
    background-color: var(--Gray-100);
    padding: 24px;

    @include media("<=desktop") {
      padding: 10px 4px;
    }

    @include media("<=720px") {
      padding: 10px 4px;
    }

    & .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0px 0px 24px 0px;

      & h2 {
        color: var(--Blue-900-Darkest);
        font-size: 18px;
        font-family: "Maersk Text Bold";
        @include media("<720px") {
          margin: 10px 10px;
        }
      }
      & i {
        cursor: pointer;
        font-style: normal;
        font-size: 24px;
      }
    }

    & .body {
      display: flex;
      justify-content: space-between;
      overflow-y: auto;
      max-height: 50vh;

      & .justification-card {
        & p {
          color: var(--Blue-900-Darkest);
          font-size: 16px;
          font-family: "Maersk Text";
          background-color: var(--Gray-50);
          padding: 16px;
          max-width: 800px;
        }
      }

      & .left-section {
        display: flex;
        flex-direction: column;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        background-color: var(--Gray-50);
        max-width: 176px;
        width: 100%;
        overflow-y: auto;
        cursor: default;

        @include media("<=desktop") {
          display: none;
        }

        & .left-section-header {
          font-family: "Maersk Text Bold";
          font-size: 14px;
          line-height: 16px;
          color: var(--Gray-900);
          font-weight: 700;
          padding-left: 24px;
          padding-top: 24px;
        }

        & .left-section-title {
          font-family: "Maersk Text";
          font-size: 14px;
          line-height: 16px;
          color: var(--Blue-900-Darkest);

          & .left-section-tab {
            box-sizing: border-box;
            padding: 12px 24px;
            white-space: nowrap;
          }
        }
      }

      & .right-section {
        margin-left: 16px;
        margin-right: 24px;
        background-color: var(--Gray-50);
        overflow-y: auto;
        box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.04),
          0px 1px 18px rgba(0, 0, 0, 0.03), 0px 6px 10px rgba(0, 0, 0, 0.03);
        border-radius: 4px;
        width: 100%;

        & .no-items-found {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
          padding: 1rem 2rem;
        }
      }

      & .right-section-header {
        font-family: "Maersk Text Bold";
        font-size: 14px;
        line-height: 16px;
        color: var(--Blue-900-Darkest);
        font-weight: 700;
        padding-left: 24px;
        padding-top: 24px;
      }

      & .right-section-search {
        margin: 12px 22px 0px 26px;
      }

      & .right-section-title {
        display: flex;
        flex-direction: column;

        & .column-heads {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid var(--Gray-400);
          box-sizing: border-box;
          box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
          margin: 12px 22px 0px 26px;
          padding: 8px 16px;

          &:last-child {
            margin-bottom: 15px;
          }

          & .column-icons {
            cursor: pointer;
            display: flex;
            align-content: center;

            & span:first-child {
              margin-right: 0.5rem;
            }

            & .toggle-eye {
              position: relative;
              top: 3px;
              right: 5px;

              &:focus {
                outline: none;
              }
            }

            & .toggle-lock {
              &:focus {
                outline: none;
              }

              @include media("<720px") {
                display: none;
              }
            }

            & .disabled {
              cursor: not-allowed;
            }
          }

          & .column-title {
            font-family: "Maersk Text";
            font-size: 14px;
            color: var(--Blue-900-Darkest);
            cursor: pointer;
            display: flex;
            align-items: center;

            span {
              margin-right: 0.5rem;
            }
          }
        }
      }
    }

    & .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 0 0 0;

      & .restore-defaults {
        color: var(--Blue-600-Dark);
      }

      @include media("<720px") {
        margin: 10px 10px;
      }

      & .lCol {
        span {
          font-size: 14px;
          &:hover {
            cursor: pointer;
          }
        }
      }

      & .rCol {
        display: flex;
      }
    }
  }
}
</style>

<style lang="scss">
.right-section-search {
  & .form-input__field {
    background-color: var(--Gray-200);
  }
}
</style>
